import React from 'react';
import PropTypes from 'prop-types';
import anime from 'animejs';
import axios from 'axios';
import qs from 'qs';
import UrlHelper from 'foundation/url-helper';
import historyController from 'general/js/history-controller';
import pageSpinner from 'general/js/page-spinner';
import api from 'general/js/api';
import { getPathWithQueryString } from './listing-helper';

const PAGINATION_DEFAULT = {
    perPage: 0,
    totalCount: 0,
    currentPage: 1,
    pageCount: 0,
};

const LISTING_ID = 'listing';

export default class Listing extends React.PureComponent {
    constructor(props) {
        super(props);

        this.hasInitialItems = Array.isArray(props.options.initialItems);

        const pagination = props.options.pagination || PAGINATION_DEFAULT;

        this.state = {
            items: this.hasInitialItems ? props.options.initialItems : [],
            filters: this.getInitialFilters(),
            pagination,
            isLoadingMore: false,
            isFiltersApplied: false,
            isInitialRequestFulfilled: this.hasInitialItems,
        };

        this.currentPage = pagination.currentPage;
        this.lastRequestCancel = null;

        this.onFiltersChange = this.onFiltersChange.bind(this);
        this.onLoadMore = this.onLoadMore.bind(this);
        this.animateChange = this.animateChange.bind(this);

        // baseUrl is the url without filtering params
        // for example if we have 2 filters: "order" and "title" and the url is /test/products/?categoryId=25&title=beer
        // we will store /test/products/?categoryId=25 as a baseUrl and will be combining our filters with it
        this.baseUrl = getPathWithQueryString(window.location, Object.keys(this.props.defaultFilters));
        historyController.register(LISTING_ID, this.onHistoryChange);
    }

    componentDidMount() {
        if (!this.hasInitialItems) {
            this.loadItems();
        }

        this.listElement = this.getListElement();
    }

    componentDidUpdate() {
        this.listElement = this.getListElement();
    }

    focusSection() {
        if (this.listElement) {
            this.listElement.focus();
        }
    }

    // it has been disabled, but it can be an improvement
    //focusOnResult() {
    //    if (this.listElement) {
    //        const firstLink = this.listElement.querySelector('a');
    //        if (firstLink) {
    //            firstLink.focus();
    //        }
    //    }
    //}

    getListElement() {
        return this.element.querySelector('[data-content]');
    }

    animateChange(changeAction) {
        anime.timeline()
            .add({
                targets: this.listElement,
                opacity: [1, 0],
                easing: 'easeInOutQuart',
                duration: 100,
                complete: () => {
                    if (changeAction) {
                        changeAction();
                    }
                }
            })
            .add({
                targets: this.listElement,
                opacity: [0, 1],
                easing: 'easeInOutQuart',
                duration: 400
            });
    }

    loadItems() {
        this.focusSection();
        pageSpinner.show({ fade: true });
        this.getItems().then((result) => {
            this.animateChange(() => {
                this.setState({
                    items: result.items,
                    pagination: result.pagination,
                    isInitialRequestFulfilled: true,
                    isFiltersApplied: true,
                });
                this.currentPage = result.pagination.currentPage;
                pageSpinner.hide();
                // we can focus on first result here with focusOnResult method
            });
        }, (error) => {
            // hide after error only when our request has been canceled
            if (!axios.isCancel(error)) {
                pageSpinner.hide();
            }
        });
    }

    loadNextPageItems() {
        if (!this.state.isLoadingMore) {
            this.setState({ isLoadingMore: true }, () => {
                this.getItems(this.currentPage + 1).then((result) => {
                    this.setState({
                        items: [...this.state.items, ...result.items],
                        pagination: result.pagination
                    });
                    this.currentPage = result.pagination.currentPage;
                    this.setState({ isLoadingMore: false });
                }, () => {
                    this.setState({ isLoadingMore: false });
                });
            });
        }
    }

    setFilters(filters, apply = true) {
        const newState = {
            filters,
        };

        if (!apply) {
            // set filters without applying
            newState.isFiltersApplied = false;
        }

        this.setState(newState, () => {
            if (apply) {
                this.loadItems();
            }
        });
    }

    /**
     * parse initial location to extract currently applied query parameters
     * which are the part of the listing's filters
     * @returns {*}
     */
    getInitialFilters() {
        return this.getFiltersFromLocation();
    }

    /**
     * Analyzing the location to apply filters from the browser's query
     */
    onHistoryChange = () => {
        this.setFilters(this.getFiltersFromLocation());
    }

    getFiltersFromLocation() {
        return { ...this.props.defaultFilters, ...this.getFiltersByLocation(document.location) };
    }

    onFiltersChange(filterObject, apply = true) {
        const passedFilters = this.getValidFilters(filterObject);
        const updatedFilters = { ...this.state.filters, ...passedFilters };
        this.setFilters(updatedFilters, apply);

        if (apply) {
            let url = this.baseUrl;
            const searchableFilters = UrlHelper.getSearchableParams(updatedFilters, this.props.defaultFilters);
            if (Object.keys(searchableFilters).length > 0) {
                url = UrlHelper.getUrlByParams(url, searchableFilters);
            }

            historyController.pushState(LISTING_ID, null, url, updatedFilters);
        }
    }

    onLoadMore() {
        this.loadNextPageItems();
    }

    getItems(page = 1) {
        const customParams = this.props.options.customParams || {};
        const params = { ...this.state.filters, ...customParams };
        params.currentPage = page;

        const searchableFilters = UrlHelper.getSearchableParams(params, this.props.defaultFilters);

        if (this.lastRequestCancel) {
            this.lastRequestCancel('Request was canceled');
            this.lastRequestCancel = null;
        }

        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        this.lastRequestCancel = source.cancel.bind(source);

        return api.get(this.props.options.endpointUrl, {
            params: {
                ...searchableFilters,
                headers: this.props.options.customHeaders ? this.props.options.customHeaders : {},
                cancelToken: source.token,
            },
            paramsSerializer: (params1) => {
                return qs.stringify(params1);
            },
        }).then((result) => {
            this.lastRequestCancel = null;
            return result.data;
        }, (error) => {
            if (!axios.isCancel(error)) {
                this.lastRequestCancel = null;
            }
            return Promise.reject(error);
        });
    }

    /**
     * We are parsing location but count only filters that we are aware of
     * @param location
     */
    getFiltersByLocation(location) {
        const search = UrlHelper.getSearchFromLocation(location);
        return this.getValidFilters(search);
    }

    getValidFilters(filters) {
        /* eslint-disable */
        const result = {};
        for (const i in filters) {
            if (i in this.props.defaultFilters) {
                result[i] = filters[i];
            }
        }
        return result;
    }

    render() {
        return (
            <div ref={(el) => {
                this.element = el;
            }}>
                {this.props.render({
                    isInitialRequestFulfilled: this.state.isInitialRequestFulfilled,
                    filters: this.state.filters,
                    isFiltersApplied: this.state.isFiltersApplied,
                    items: this.state.items,
                    pagination: this.state.pagination,
                    onFiltersChange: this.onFiltersChange,
                    animateChange: this.animateChange,
                    onLoadMore: this.onLoadMore,
                    isLoadingMore: this.state.isLoadingMore,
                })}
            </div>
        );
    }
}

Listing.propTypes = {
    options: PropTypes.object.isRequired,
    defaultFilters: PropTypes.object.isRequired,
    render: PropTypes.func.isRequired,
};
